// @story: libs/storybook/stories/modules/auth/entity-access-dialog.stories.ts
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, map, of } from 'rxjs';
import {
  AccordionItemComponent,
  Avatar,
  Badge,
  Card,
  CardSearchComponent,
  ConfirmDialogService,
  DrawerComponent,
  SelectionRowComponent,
  TooltipDirective,
  ZagButton,
  ZagIcon,
  ZagSwitch,
  type AvatarVariant,
  type SearchScopeOption,
} from '@zag/ui';
import type { EntityCapabilities, EntityGrantee, EntityGrantSet, EntityType, GranteeKind, GranteeSearchItem } from '@zag/auth/models';
import { ENTITY_FAMILIES } from '@zag/auth/models';
import { FeatureAccessClientService } from '@zag/features/ui';
import { EntityAccessService, isFullModeRejection } from '../entity-access.service';
import { PermissionService } from '../permission.service';
import { PermissionsManagementService } from '../permissions-management.service';

export interface EntityAccessDialogData {
  entityType: EntityType;
  entityId: string;
  entityLabel?: string;
  userId?: string;
  userLabel?: string;
}

export interface EntityAccessDialogResult {
  saved: boolean;
  fullMode?: boolean;
}

export type EntityAccessDialogCloseResult = boolean | EntityAccessDialogResult;

export type RecipientScope = 'all' | 'groups' | 'users';

const RECIPIENT_SCOPE_IDS: readonly RecipientScope[] = ['groups', 'users', 'all'];

const RECIPIENT_SCOPE_KIND: Record<RecipientScope, GranteeKind | null> = { all: null, groups: 'group', users: 'user' };

const RECIPIENT_SCOPE_LABEL_KEY: Record<RecipientScope, string> = {
  all: 'entityAccess.dialog.scopeAll',
  groups: 'entityAccess.dialog.scopeGroups',
  users: 'entityAccess.dialog.scopeUsers',
};

interface UserGrantWorkingSet {
  permissions: ReadonlySet<string>;
  originalPermissions: ReadonlySet<string>;
  etag: string;
  eligibleForDirectGrant: boolean;
}

export interface PermissionSection {
  titleKey: string | null;
  hintKey: string | null;
  permissions: readonly string[];
}

// Keeps permissions of one boundary (employees, timetracking, ...) adjacent without rendering group headers.
function orderByBoundary(permissions: readonly string[]): string[] {
  const boundaries = [...new Set(permissions.map(permission => permission.split(':')[0]))];
  return boundaries.flatMap(boundary => permissions.filter(permission => permission.split(':')[0] === boundary));
}

type PermissionBundle = Pick<PermissionSection, 'permissions'>;

interface DerivedSection {
  family: EntityType;
  titleKey: string;
  hintKey: string;
  admission: string;
}

// Families whose grants on this entity cascade to dynamically resolved members.
const DERIVED_SECTIONS: Partial<Record<EntityType, readonly DerivedSection[]>> = {
  team: [
    {
      family: 'person',
      titleKey: 'entityAccess.dialog.membersSection',
      hintKey: 'entityAccess.dialog.membersHint',
      admission: 'employees:read',
    },
  ],
  department: [
    {
      family: 'team',
      titleKey: 'entityAccess.dialog.departmentTeamsSection',
      hintKey: 'entityAccess.dialog.departmentTeamsHint',
      admission: 'departments:read',
    },
    {
      family: 'person',
      titleKey: 'entityAccess.dialog.departmentMembersSection',
      hintKey: 'entityAccess.dialog.departmentMembersHint',
      admission: 'departments:read',
    },
  ],
};

const OWN_SECTION_TITLE_KEYS: Partial<Record<EntityType, string>> = {
  team: 'entityAccess.dialog.teamSection',
  department: 'entityAccess.dialog.departmentSection',
};

// Groups first, then alphabetical by label.
function compareGranteesForAllScope(a: GranteeSearchItem, b: GranteeSearchItem): number {
  if (a.kind !== b.kind) return a.kind === 'group' ? -1 : 1;
  return a.label.localeCompare(b.label);
}

export function granteeKey(grantee: EntityGrantee): string {
  return `${grantee.kind}:${grantee.id}`;
}

function keyToGrantee(key: string): EntityGrantee {
  const separator = key.indexOf(':');
  const kind = key.slice(0, separator);
  return { kind: kind === 'group' ? 'group' : 'user', id: key.slice(separator + 1) };
}

@Component({
  selector: 'sc-entity-access-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    TranslatePipe,
    AccordionItemComponent,
    Avatar,
    Badge,
    Card,
    CardSearchComponent,
    DrawerComponent,
    SelectionRowComponent,
    TooltipDirective,
    ZagButton,
    ZagIcon,
    ZagSwitch,
  ],
  templateUrl: './entity-access-dialog.component.html',
  styleUrl: './entity-access-dialog.component.scss',
})
export class EntityAccessDialogComponent {
  private readonly dialogRef = inject(DialogRef<EntityAccessDialogCloseResult>);
  protected readonly data = inject<EntityAccessDialogData>(DIALOG_DATA);
  private readonly access = inject(EntityAccessService);
  private readonly permissions = inject(PermissionService);
  private readonly catalog = inject(PermissionsManagementService);
  private readonly featureAccess = inject(FeatureAccessClientService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  protected readonly selectableMode = this.data.userId === undefined;
  private entityLabelLoaded = this.data.entityLabel !== undefined;
  private recipientRequestId = 0;

  protected readonly rows: readonly string[] = ENTITY_FAMILIES[this.data.entityType].grantablePermissions;
  private readonly derivedSections = DERIVED_SECTIONS[this.data.entityType] ?? [];
  protected readonly combinedRows: readonly string[] = [
    ...this.rows,
    ...this.derivedSections.flatMap(section => ENTITY_FAMILIES[section.family].grantablePermissions),
  ];
  protected readonly permissionSections: readonly PermissionSection[] =
    this.derivedSections.length === 0
      ? [{ titleKey: null, hintKey: null, permissions: orderByBoundary(this.rows) }]
      : [
          { titleKey: OWN_SECTION_TITLE_KEYS[this.data.entityType] ?? null, hintKey: null, permissions: orderByBoundary(this.rows) },
          ...this.derivedSections.map(section => ({
            titleKey: section.titleKey,
            hintKey: section.hintKey,
            permissions: orderByBoundary(ENTITY_FAMILIES[section.family].grantablePermissions),
          })),
        ];
  private readonly expandedSections = signal<ReadonlySet<string>>(
    new Set(this.permissionSections.slice(0, 1).map(section => section.titleKey ?? ''))
  );
  protected readonly managePermission: string | null = this.access.managePermissionFor(this.data.entityType);
  protected readonly managePermissionLabel = this.managePermission ?? '';
  protected readonly entityLabel = signal(this.data.entityLabel ?? this.translate.instant('entityAccess.panel.restricted'));
  protected readonly entityLabelLoading = signal(!this.entityLabelLoaded);

  protected readonly searchTerm = signal('');
  protected readonly recipientScope = signal<RecipientScope>('groups');
  protected readonly grantees = signal<readonly GranteeSearchItem[]>([]);
  protected readonly granteesLoading = signal(false);

  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  protected readonly recipientScopes = computed<SearchScopeOption[]>(() => {
    this.currentLang();
    return RECIPIENT_SCOPE_IDS.map(id => ({ id, label: this.translate.instant(RECIPIENT_SCOPE_LABEL_KEY[id]) }));
  });

  protected readonly visibleGrantees = computed<readonly GranteeSearchItem[]>(() => {
    const kind = RECIPIENT_SCOPE_KIND[this.recipientScope()];
    const granted = this.grantedGranteeKeys();
    const items = (kind === null ? this.grantees() : this.grantees().filter(grantee => grantee.kind === kind)).filter(
      grantee => grantee.eligibleForDirectGrant || granted.has(granteeKey({ kind: grantee.kind, id: grantee.id }))
    );
    if (this.recipientScope() !== 'all') return items;
    return [...items].sort(compareGranteesForAllScope);
  });

  protected onScopeChange(scope: string | undefined): void {
    if (scope === 'all' || scope === 'groups' || scope === 'users') this.recipientScope.set(scope);
  }
  protected readonly grantedGranteeKeys = signal<ReadonlySet<string>>(new Set());
  private readonly knownLabels = signal<ReadonlyMap<string, string>>(new Map());

  protected readonly selectedGranteeKeys = signal<ReadonlySet<string>>(
    this.data.userId !== undefined ? new Set([granteeKey({ kind: 'user', id: this.data.userId })]) : new Set()
  );
  private readonly workingSets = signal<ReadonlyMap<string, UserGrantWorkingSet>>(new Map());
  private readonly loadingGranteeKeys = signal<ReadonlySet<string>>(new Set());

  protected readonly isSaving = signal(false);
  protected readonly capabilities = signal<EntityCapabilities | null>(null);
  protected readonly capabilitiesLoading = signal(true);
  protected readonly conflict = signal(false);
  protected readonly revoked = signal(false);
  private readonly saveRejectedFullMode = signal(false);
  protected readonly loadError = signal<string | null>(null);
  protected readonly saveError = signal<string | null>(null);

  // Derived from the currently selected grantees' working sets, not sticky —
  // deselecting a full-mode recipient (or picking a different, eligible one)
  // restores editing instead of permanently locking the dialog. Users and
  // groups behave the same: an ineligible working set trips full-mode.
  protected readonly fullMode = computed(() => {
    if (this.saveRejectedFullMode()) return true;
    const keys = [...this.selectedGranteeKeys()];
    if (keys.length === 0) return false;
    return keys.some(key => this.workingSets().get(key)?.eligibleForDirectGrant === false);
  });

  protected readonly hasSelection = computed(() => this.selectedGranteeKeys().size > 0);
  protected readonly isLoadingSelected = computed(() => this.loadingGranteeKeys().size > 0);
  // Entity label + capabilities gate the dialog chrome (header/footer states).
  // The recipient list intentionally stays mounted across grantee reloads: tearing
  // it down per search would destroy the focused search input mid-typing and
  // retrigger its mount-time effects into a reload loop.
  protected readonly isLoading = computed(() => this.entityLabelLoading() || this.capabilitiesLoading());
  private readonly featureKeyById = computed(() => new Map(this.catalog.permissions().map(p => [p.id, p.featureKey ?? null] as const)));
  protected readonly unlicensed = computed<ReadonlySet<string>>(() => {
    if (!this.featureAccess.featuresLoaded()) return new Set<string>();
    const byId = this.featureKeyById();
    return new Set(
      this.combinedRows.filter(permission => {
        const feature = byId.get(permission) ?? null;
        return feature !== null && !this.featureAccess.hasFeature(feature);
      })
    );
  });
  protected readonly hasGrants = computed(() =>
    [...this.selectedGranteeKeys()].some(key => (this.workingSets().get(key)?.originalPermissions.size ?? 0) > 0)
  );
  protected readonly nonOwnerDenied = computed(
    () => this.data.entityType === 'calendar' && (this.capabilities()?.isOwner ?? null) !== true
  );
  protected readonly canEdit = computed(() => {
    const caps = this.capabilities();
    if (!caps || this.fullMode() || this.conflict() || this.revoked()) return false;
    if (this.managePermission === null) return false;
    return caps.canManageAccess && !caps.readOnly && this.permissions.hasPermission(this.managePermission) && !this.nonOwnerDenied();
  });
  protected readonly readOnly = computed(
    () => this.capabilities()?.readOnly === true && !this.fullMode() && !this.conflict() && !this.revoked() && !this.nonOwnerDenied()
  );
  protected readonly rowsDisabled = computed(
    () => this.isLoading() || this.isLoadingSelected() || this.isSaving() || this.conflict() || this.revoked()
  );
  protected readonly avatarVariant = computed<AvatarVariant>(() => (this.data.entityType === 'person' ? 'brand' : 'info'));
  protected readonly showReload = computed(() => this.conflict() || this.revoked());
  protected readonly showRemoveAll = computed(() => this.data.entityType === 'person' && this.hasGrants());
  protected readonly viewerPermissions = computed(() => this.capabilities()?.grantedPermissions ?? []);
  protected readonly canSave = computed(
    () => this.hasSelection() && this.canEdit() && !this.isLoading() && !this.isLoadingSelected() && !this.isSaving() && !this.loadError()
  );

  protected readonly selectedLabel = computed(() => {
    const keys = [...this.selectedGranteeKeys()];
    if (keys.length === 0) return '';
    if (keys.length === 1) {
      const key = keys[0];
      if (!this.selectableMode) return this.data.userLabel ?? key;
      return this.knownLabels().get(key) ?? key;
    }
    return this.translate.instant('entityAccess.dialog.selectedCount', { count: keys.length });
  });

  protected readonly subtitle = computed((): { key: string; params: Record<string, string> } => {
    if (this.conflict()) return { key: 'entityAccess.dialog.subtitleConflict', params: {} };
    if (this.revoked()) return { key: 'entityAccess.dialog.subtitleRevoked', params: {} };
    if (this.fullMode()) return { key: 'entityAccess.dialog.subtitleFullMode', params: {} };
    if (this.nonOwnerDenied() || this.readOnly()) return { key: 'entityAccess.dialog.subtitleReadOnly', params: {} };
    if (!this.hasSelection()) return { key: 'entityAccess.host.pickUserTitle', params: {} };
    return {
      key: 'entityAccess.dialog.subtitleGranted',
      params: { entityType: this.data.entityType, user: this.selectedLabel() },
    };
  });

  constructor() {
    this.loadEntityLabel();
    this.loadCapabilities();
    if (this.selectableMode) {
      this.loadGrantees();
      this.loadBulkGrants();
    } else if (this.data.userId !== undefined) {
      this.ensureWorkingSet({ kind: 'user', id: this.data.userId });
    }
  }

  protected isGranted(grantee: EntityGrantee): boolean {
    return this.grantedGranteeKeys().has(granteeKey(grantee));
  }

  // Full-mode recipients hold role-level access, so an empty direct-grant set
  // means "full access", not "no access". Applies to users and groups alike.
  protected granteeBadgeKey(grantee: GranteeSearchItem): string {
    if (this.isGranted(grantee)) return 'entityAccess.dialog.userGranted';
    if (!grantee.eligibleForDirectGrant) return 'entityAccess.dialog.userFullAccess';
    if (grantee.kind === 'group' && grantee.allMembersFullMode === true) return 'entityAccess.dialog.userFullAccess';
    return 'entityAccess.dialog.userNotGranted';
  }

  protected granteeBadgeColor(grantee: GranteeSearchItem): 'success' | 'info' | 'neutral' {
    if (this.isGranted(grantee)) return 'success';
    if (!grantee.eligibleForDirectGrant) return 'info';
    if (grantee.kind === 'group' && grantee.allMembersFullMode === true) return 'info';
    return 'neutral';
  }

  protected granteeDescription(grantee: GranteeSearchItem): string | null {
    if (grantee.kind !== 'group' || grantee.memberCount === null) return null;
    return this.translate.instant('entityAccess.dialog.groupMembers', { count: grantee.memberCount });
  }

  protected isGranteeSelected(grantee: EntityGrantee): boolean {
    return this.selectedGranteeKeys().has(granteeKey(grantee));
  }

  protected isSelected(permission: string): boolean {
    const keys = this.selectedGranteeKeys();
    if (keys.size === 0) return false;
    for (const key of keys) {
      const ws = this.workingSets().get(key);
      if (!ws || !ws.permissions.has(permission)) return false;
    }
    return true;
  }

  protected isUnlicensed(permission: string): boolean {
    return this.unlicensed().has(permission);
  }

  private readonly requiresById = computed(() => {
    const map = new Map<string, string | null>();
    for (const entry of this.catalog.permissions() as Array<Record<string, unknown>>) {
      map.set(entry['id'] as string, (entry['requiresPermissionId'] as string | null) ?? null);
    }
    for (const id of this.combinedRows) {
      if ((map.get(id) ?? null) !== null) continue;
      const fallback = id.endsWith(':update') ? `${id.slice(0, -':update'.length)}:read` : null;
      if (fallback !== null && (this.combinedRows as readonly string[]).includes(fallback)) map.set(id, fallback);
      else if (!map.has(id)) map.set(id, null);
    }
    this.addAdmissionPrerequisites(map);
    return map;
  });

  // Derived grants are inert without the container's admission row, so it becomes the prerequisite of every section root.
  private addAdmissionPrerequisites(map: Map<string, string | null>): void {
    const rows = this.combinedRows as readonly string[];
    for (const section of this.derivedSections) {
      for (const id of ENTITY_FAMILIES[section.family].grantablePermissions) {
        const parent = map.get(id) ?? null;
        if (id !== section.admission && (parent === null || !rows.includes(parent))) map.set(id, section.admission);
      }
    }
  }

  private dependentsOf(id: string): string[] {
    const requires = this.requiresById();
    return (this.combinedRows as readonly string[]).filter(candidate => requires.get(candidate) === id);
  }

  private applyPermissionCascade(permissions: ReadonlySet<string>, permission: string, checked: boolean): Set<string> {
    const next = new Set(permissions);
    const requires = this.requiresById();
    if (checked) {
      next.add(permission);
      let cursor = requires.get(permission) ?? null;
      const seen = new Set<string>();
      while (cursor !== null && !seen.has(cursor)) {
        seen.add(cursor);
        if ((this.combinedRows as readonly string[]).includes(cursor)) next.add(cursor);
        cursor = requires.get(cursor) ?? null;
      }
      return next;
    }
    next.delete(permission);
    const queue = [...this.dependentsOf(permission)];
    const seen = new Set(queue);
    while (queue.length > 0) {
      const dependent = queue.shift() as string;
      next.delete(dependent);
      for (const transitive of this.dependentsOf(dependent)) {
        if (!seen.has(transitive)) {
          seen.add(transitive);
          queue.push(transitive);
        }
      }
    }
    return next;
  }

  protected isSectionExpanded(section: PermissionSection): boolean {
    return this.expandedSections().has(section.titleKey ?? '');
  }

  protected onSectionExpandedChange(section: PermissionSection, expanded: boolean): void {
    const next = new Set(this.expandedSections());
    if (expanded) next.add(section.titleKey ?? '');
    else next.delete(section.titleKey ?? '');
    this.expandedSections.set(next);
  }

  protected selectedInSection(section: PermissionSection): number {
    return section.permissions.filter(permission => this.isSelected(permission)).length;
  }

  protected isGroupChecked(group: PermissionBundle): boolean {
    const keys = this.selectedGranteeKeys();
    if (keys.size === 0) return false;
    for (const key of keys) {
      const ws = this.workingSets().get(key);
      if (!ws) return false;
      if (!group.permissions.every(permission => ws.permissions.has(permission))) return false;
    }
    return true;
  }

  protected isGroupDisabled(group: PermissionBundle): boolean {
    if (this.rowsDisabled() || !this.canEdit()) return true;
    return group.permissions.every(permission => this.isUnlicensed(permission));
  }

  protected onGroupToggle(group: PermissionBundle, checked: boolean): void {
    if (!this.canEdit() || this.rowsDisabled()) return;
    const keys = this.selectedGranteeKeys();
    const next = new Map(this.workingSets());
    for (const key of keys) {
      const ws = next.get(key);
      if (!ws) continue;
      let permissions = new Set(ws.permissions);
      const ordered = checked ? [...group.permissions] : [...group.permissions].reverse();
      for (const permission of ordered) {
        if (this.isUnlicensed(permission)) continue;
        permissions = this.applyPermissionCascade(permissions, permission, checked);
      }
      next.set(key, { ...ws, permissions });
    }
    this.workingSets.set(next);
  }

  protected isRowDisabled(permission: string): boolean {
    return this.rowsDisabled() || !this.canEdit() || this.isUnlicensed(permission);
  }

  protected onPermissionChange(permission: string, checked: boolean): void {
    if (!this.canEdit() || this.isUnlicensed(permission) || this.rowsDisabled()) return;
    const keys = this.selectedGranteeKeys();
    const next = new Map(this.workingSets());
    for (const key of keys) {
      const ws = next.get(key);
      if (!ws) continue;
      next.set(key, { ...ws, permissions: this.applyPermissionCascade(ws.permissions, permission, checked) });
    }
    this.workingSets.set(next);
  }

  protected onSearchChange(term: string): void {
    if (term === this.searchTerm()) return;
    this.searchTerm.set(term);
    this.loadGrantees(term);
  }

  protected onToggleGrantee(grantee: EntityGrantee, checked: boolean): void {
    this.saveRejectedFullMode.set(false);
    const key = granteeKey(grantee);
    const next = new Set(this.selectedGranteeKeys());
    if (checked) {
      next.add(key);
      this.ensureWorkingSet(grantee);
    } else {
      next.delete(key);
    }
    this.selectedGranteeKeys.set(next);
  }

  protected onCancel(): void {
    this.dialogRef.close(this.fullMode() ? { saved: false, fullMode: true } : false);
  }

  protected onEditorCancel(): void {
    this.onCancel();
  }

  protected onReload(): void {
    this.conflict.set(false);
    this.revoked.set(false);
    this.saveRejectedFullMode.set(false);
    this.loadError.set(null);
    this.loadEntityLabel();
    this.loadCapabilities();
    const keys = [...this.selectedGranteeKeys()];
    this.workingSets.set(new Map());
    for (const key of keys) this.ensureWorkingSet(keyToGrantee(key));
    if (this.selectableMode) {
      this.loadGrantees(this.searchTerm());
      this.loadBulkGrants();
    }
  }

  protected onSave(): void {
    if (!this.canSave()) return;
    const targets = [...this.selectedGranteeKeys()].map(keyToGrantee);
    const { entityType, entityId } = this.data;
    this.isSaving.set(true);
    this.saveError.set(null);
    forkJoin(
      targets.map(grantee => {
        const key = granteeKey(grantee);
        const ws = this.workingSets().get(key);
        return this.access.replaceGrants(entityType, entityId, grantee, [...(ws?.permissions ?? [])].sort(), ws?.etag ?? '*').pipe(
          map((grants): { grants: EntityGrantSet; error: null } => ({ grants, error: null })),
          catchError((error: HttpErrorResponse) => of({ grants: null as EntityGrantSet | null, error }))
        );
      })
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(results => {
        this.isSaving.set(false);
        const failure = results.find((result): result is { grants: null; error: HttpErrorResponse } => result.error !== null);
        if (failure) {
          this.handleWriteError(failure.error);
          return;
        }
        this.commitSavedResults(results.map(result => result.grants).filter((grants): grants is EntityGrantSet => grants !== null));
        this.dialogRef.close(true);
      });
  }

  protected async onRemoveAll(): Promise<void> {
    if (!this.canSave()) return;
    const { entityType, entityId } = this.data;
    const targets = [...this.selectedGranteeKeys()].filter(key => (this.workingSets().get(key)?.originalPermissions.size ?? 0) > 0);
    if (targets.length === 0) return;
    const confirmed = await this.confirmDialog.confirm({
      title: 'entityAccess.dialog.removeAllConfirm.title',
      message: 'entityAccess.dialog.removeAllConfirm.message',
      confirmText: 'entityAccess.dialog.removeAll',
      destructive: true,
    });
    if (!confirmed) return;
    this.isSaving.set(true);
    this.saveError.set(null);
    forkJoin(
      targets.map(key => {
        const ws = this.workingSets().get(key);
        return this.access.deleteGrants(entityType, entityId, keyToGrantee(key), ws?.etag ?? '*').pipe(
          map(() => ({ error: null }) as { error: HttpErrorResponse | null }),
          catchError((error: HttpErrorResponse) => of({ error }))
        );
      })
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(results => {
        this.isSaving.set(false);
        const failure = results.find((result): result is { error: HttpErrorResponse } => result.error !== null);
        if (failure) {
          this.handleWriteError(failure.error);
          return;
        }
        this.dialogRef.close(true);
      });
  }

  private commitSavedResults(results: EntityGrantSet[]): void {
    const next = new Map(this.workingSets());
    for (const grants of results) {
      const permissions = new Set(grants.permissions);
      const key = granteeKey({ kind: grants.kind, id: (grants.groupId ?? grants.userId) as string });
      next.set(key, {
        permissions,
        originalPermissions: new Set(permissions),
        etag: grants.etag,
        eligibleForDirectGrant: grants.eligibleForDirectGrant,
      });
    }
    this.workingSets.set(next);
  }

  private loadEntityLabel(): void {
    if (this.entityLabelLoaded) return;
    this.entityLabelLoading.set(true);
    this.access
      .labelFor(this.data.entityType, this.data.entityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: label => {
          this.entityLabel.set(label);
          this.entityLabelLoaded = true;
          this.entityLabelLoading.set(false);
        },
        error: () => {
          this.entityLabel.set(this.translate.instant('entityAccess.panel.restricted'));
          this.entityLabelLoading.set(false);
        },
      });
  }

  private loadCapabilities(): void {
    this.capabilitiesLoading.set(true);
    this.access
      .getCapabilities(this.data.entityType, this.data.entityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: capabilities => {
          this.capabilities.set(capabilities);
          this.capabilitiesLoading.set(false);
        },
        error: (error: HttpErrorResponse) => {
          this.capabilitiesLoading.set(false);
          if (error.status === 403) this.revoked.set(true);
          else this.loadError.set('entityAccess.errors.generic');
        },
      });
  }

  private loadGrantees(query?: string): void {
    const requestId = ++this.recipientRequestId;
    this.granteesLoading.set(true);
    this.loadError.set(null);
    this.access
      .searchGrantees(this.data.entityType, this.data.entityId, query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          if (requestId !== this.recipientRequestId) return;
          this.grantees.set(result.items);
          this.knownLabels.update(map => {
            const next = new Map(map);
            for (const item of result.items) next.set(granteeKey({ kind: item.kind, id: item.id }), item.label);
            return next;
          });
          this.granteesLoading.set(false);
        },
        error: () => {
          if (requestId !== this.recipientRequestId) return;
          this.granteesLoading.set(false);
          this.loadError.set('entityAccess.errors.generic');
        },
      });
  }

  private loadBulkGrants(): void {
    this.access
      .listGrantsForEntity(this.data.entityType, this.data.entityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: grants => {
          this.grantedGranteeKeys.set(
            new Set(grants.map(grant => granteeKey({ kind: grant.kind, id: (grant.groupId ?? grant.userId) as string })))
          );
          const next = new Map(this.workingSets());
          for (const grant of grants) {
            const key = granteeKey({ kind: grant.kind, id: (grant.groupId ?? grant.userId) as string });
            if (next.has(key)) continue;
            const permissions = new Set(grant.permissions);
            next.set(key, {
              permissions,
              originalPermissions: new Set(permissions),
              etag: grant.etag,
              eligibleForDirectGrant: grant.eligibleForDirectGrant,
            });
          }
          this.workingSets.set(next);
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 403) this.revoked.set(true);
          else this.loadError.set('entityAccess.errors.generic');
        },
      });
  }

  private ensureWorkingSet(grantee: EntityGrantee): void {
    const key = granteeKey(grantee);
    if (this.workingSets().has(key) || this.loadingGranteeKeys().has(key)) return;
    this.fetchGrantSet(grantee);
  }

  private fetchGrantSet(grantee: EntityGrantee): void {
    const key = granteeKey(grantee);
    this.loadingGranteeKeys.update(keys => new Set(keys).add(key));
    this.access
      .getGrantSet(this.data.entityType, this.data.entityId, grantee)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: grants => {
          this.loadingGranteeKeys.update(keys => {
            const next = new Set(keys);
            next.delete(key);
            return next;
          });
          const permissions = new Set(grants.permissions);
          this.workingSets.update(map => {
            const next = new Map(map);
            next.set(key, {
              permissions,
              originalPermissions: new Set(permissions),
              etag: grants.etag,
              eligibleForDirectGrant: grants.eligibleForDirectGrant,
            });
            return next;
          });
        },
        error: (error: HttpErrorResponse) => {
          this.loadingGranteeKeys.update(keys => {
            const next = new Set(keys);
            next.delete(key);
            return next;
          });
          if (error.status === 403) this.revoked.set(true);
          else if (error.status === 404) this.loadError.set('entityAccess.errors.phantom');
          else this.loadError.set('entityAccess.errors.generic');
        },
      });
  }

  private handleWriteError(error: HttpErrorResponse): void {
    if (error.status === 412 || error.status === 428) {
      this.conflict.set(true);
    } else if (error.status === 403) {
      this.revoked.set(true);
    } else if (error.status === 404) {
      this.loadError.set('entityAccess.errors.phantom');
    } else if (error.status === 400) {
      this.handleBadRequest(error);
    } else {
      this.saveError.set('entityAccess.errors.generic');
    }
  }

  private handleBadRequest(error: HttpErrorResponse): void {
    if (isFullModeRejection(error)) {
      this.saveRejectedFullMode.set(true);
      return;
    }
    this.saveError.set('entityAccess.errors.validation');
  }
}
